import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone, Mail } from "lucide-react";

const EMAIL = "info@heracq.com";

// Masked line-by-line reveal: each line clip-rises from below
const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.1,
      delay: 0.15 + i * 0.12,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.6 + i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function CareHero({ site }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Subtle parallax — image moves slower than scroll; caption moves slightly up
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const captionY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  return (
    <section
      ref={ref}
      data-testid={`care-hero-${site.key}`}
      id="top"
      className="relative overflow-hidden bg-[#FDFBF7] pt-32 pb-16 lg:pt-40 lg:pb-24"
    >
      {/* Organic blobs */}
      <div className="pointer-events-none absolute -top-32 -right-16 w-[460px] h-[460px] rounded-full bg-[#E6EAE3] blur-3xl opacity-70" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 w-[340px] h-[340px] rounded-full bg-[#F3EFE9] blur-3xl opacity-70" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="eyebrow"
          >
            {site.heroEyebrow}
          </motion.div>

          <h1
            data-testid="care-hero-headline"
            className="font-serif-display mt-6 text-[clamp(2.75rem,8vw,6.5rem)] leading-[0.98] tracking-tight text-[#1A2621]"
          >
            {site.heroLines.map((text, i) => (
              <span
                key={i}
                className="block overflow-hidden pb-2"
                aria-hidden={false}
              >
                <motion.span
                  className={`block ${i === 1 ? "italic text-[#284A3B]" : ""}`}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  custom={i}
                >
                  {text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0}
            data-testid="care-hero-sub"
            className="mt-8 max-w-xl text-lg md:text-xl text-[#4A5D54] leading-relaxed"
          >
            {site.heroSub}
          </motion.p>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={1}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              data-testid="care-hero-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C86A51] hover:bg-[#B35A42] text-white rounded-full px-8 py-4 font-medium transition-colors shadow-sm group"
            >
              Request Care
              <ArrowDownRight
                size={18}
                className="transition-transform group-hover:translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>

            {site.phoneHref ? (
              <a
                href={site.phoneHref}
                data-testid="care-hero-phone"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#284A3B] text-[#284A3B] hover:bg-[#284A3B] hover:text-[#FDFBF7] rounded-full px-8 py-4 font-medium transition-colors"
              >
                <Phone size={16} /> {site.phone}
              </a>
            ) : (
              <a
                href={`mailto:${EMAIL}?subject=Care%20Inquiry`}
                data-testid="care-hero-email"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#284A3B] text-[#284A3B] hover:bg-[#284A3B] hover:text-[#FDFBF7] rounded-full px-8 py-4 font-medium transition-colors"
              >
                <Mail size={16} /> {EMAIL}
              </a>
            )}
          </motion.div>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-[#4A5D54]"
          >
            {["Vetted caregivers", "Licensed & insured", "Serving every day of the week"].map(
              (t) => (
                <div key={t} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#C86A51]" />
                  {t}
                </div>
              ),
            )}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden border border-[#D5DACF] shadow-sm aspect-[4/5] bg-[#E6EAE3]"
          >
            <motion.img
              src={site.heroImage}
              alt={site.heroImageAlt}
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-[115%] object-cover will-change-transform"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A2621]/55 via-transparent to-transparent" />
            <motion.div
              style={{ y: captionY }}
              className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-[#FDFBF7]"
            >
              <div className="font-serif-display text-2xl md:text-3xl max-w-[280px] leading-tight">
                {site.productName}
              </div>
              <div className="text-xs uppercase tracking-[0.22em] opacity-80">
                est. 2025
              </div>
            </motion.div>
          </motion.div>

          {/* Floating stat chip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -left-5 -bottom-6 bg-white rounded-2xl border border-[#D5DACF] shadow-md p-5 max-w-[220px] hidden md:block"
          >
            <div className="font-serif-display text-3xl text-[#284A3B] leading-none">
              24/7
            </div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#4A5D54] mt-2">
              Care, when it matters
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
